import styled, { keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const spin = keyframes`
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
`;

export const ButtonSpinner = styled.span`
	display: inline-block;
	position: relative;
	z-index: 1;
	width: 18px;
	height: 18px;
	margin-right: 10px;
	border: 2px solid rgba(255, 255, 255, 0.28);
	border-top-color: #fff;
	border-radius: 50%;
	animation: ${spin} 0.75s linear infinite;
	vertical-align: middle;
`;

const btnShine = keyframes`
	0% {
		transform: translateX(-140%) skewX(-22deg);
		opacity: 0;
	}
	15% {
		opacity: 1;
	}
	85% {
		opacity: 1;
	}
	100% {
		transform: translateX(240%) skewX(-22deg);
		opacity: 0;
	}
`;

const btnGlow = keyframes`
	0%,
	100% {
		box-shadow:
			0 12px 36px rgba(255, 77, 242, 0.48),
			0 0 22px rgba(255, 77, 242, 0.28),
			0 0 0 1px rgba(255, 255, 255, 0.18) inset;
	}
	50% {
		box-shadow:
			0 16px 48px rgba(255, 77, 242, 0.68),
			0 0 42px rgba(255, 211, 107, 0.38),
			0 0 0 1px rgba(255, 255, 255, 0.28) inset;
	}
`;

const skeletonPulse = keyframes`
	0%,
	100% {
		opacity: 0.35;
	}
	50% {
		opacity: 0.7;
	}
`;

export const FormContainer = styled.form`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 0;
	min-width: 0;
	max-width: 100%;
	opacity: ${(props) => (props.$processing ? 0.72 : 1)};
	pointer-events: ${(props) => (props.$processing ? 'none' : 'auto')};
	transition: opacity 0.2s ease;
`;

export const PaymentBlock = styled.div`
	display: grid;
	gap: 10px;
	min-width: 0;
	max-width: 100%;
`;

export const PaymentElementShell = styled.div`
	position: relative;
	min-height: 300px;
	max-width: 100%;
	padding: 16px;
	border-radius: 16px;
	background: rgba(4, 2, 8, 0.45);
	border: 1px solid rgba(255, 255, 255, 0.07);
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
	overflow-x: hidden;
`;

export const PaymentSkeleton = styled.div`
	position: absolute;
	inset: 16px;
	z-index: 2;
	display: grid;
	gap: 14px;
	align-content: start;
	pointer-events: none;
`;

export const SkeletonTabs = styled.div`
	display: flex;
	gap: 8px;
`;

export const SkeletonTab = styled.div`
	width: 52px;
	height: 38px;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.07);
	animation: ${skeletonPulse} 1.5s ease-in-out infinite;

	&:nth-child(1) {
		width: 58px;
		border: 1px solid rgba(255, 211, 107, 0.35);
		animation-delay: 0s;
	}

	&:nth-child(2) {
		animation-delay: 0.12s;
	}

	&:nth-child(3) {
		animation-delay: 0.24s;
	}

	&:nth-child(4) {
		animation-delay: 0.36s;
	}
`;

export const SkeletonField = styled.div`
	display: grid;
	gap: 8px;
	animation: ${skeletonPulse} 1.5s ease-in-out infinite;

	&:nth-of-type(1) {
		animation-delay: 0.3s;
	}

	&:nth-of-type(2) {
		animation-delay: 0.42s;
	}

	&:nth-of-type(3) {
		animation-delay: 0.54s;
	}
`;

export const SkeletonLabel = styled.div`
	width: 88px;
	height: 8px;
	border-radius: 999px;
	background: rgba(184, 148, 204, 0.35);
`;

export const SkeletonInput = styled.div`
	height: 44px;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.06);
	border: 1px solid rgba(255, 255, 255, 0.08);
`;

export const SkeletonFieldRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
`;

export const PaymentElementWrap = styled.div`
	min-width: 0;
	max-width: 100%;
	opacity: ${(props) => (props.$ready ? 1 : 0)};
	transition: opacity 0.28s ease;
`;

export const PayButton = styled.button`
	position: relative;
	overflow: hidden;
	isolation: isolate;
	background: ${THEME.gradientPrimary};
	color: #fff;
	border: none;
	padding: 18px 22px;
	font-family: ${THEME.fontApp};
	font-size: 1.02rem;
	font-weight: 800;
	letter-spacing: 0.03em;
	border-radius: 14px;
	cursor: pointer;
	transition:
		opacity 0.3s,
		transform 0.2s;
	width: 100%;
	animation: ${btnGlow} 2.2s ease-in-out infinite;

	&::before {
		content: '';
		position: absolute;
		top: -20%;
		bottom: -20%;
		left: 0;
		width: 42%;
		background: linear-gradient(
			90deg,
			transparent 0%,
			rgba(255, 255, 255, 0.08) 35%,
			rgba(255, 255, 255, 0.55) 50%,
			rgba(255, 255, 255, 0.08) 65%,
			transparent 100%
		);
		animation: ${btnShine} 2.4s ease-in-out infinite;
		pointer-events: none;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(255, 255, 255, 0.22) 0%,
			transparent 42%,
			rgba(0, 0, 0, 0.08) 100%
		);
		pointer-events: none;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background: rgba(255, 255, 255, 0.08);
		animation: none;
		box-shadow: none;

		&::before,
		&::after {
			display: none;
		}
	}

	&:hover:not(:disabled) {
		transform: translateY(-2px);
	}

	span {
		position: relative;
		z-index: 1;
	}
`;

export const ErrorMessage = styled.div`
	padding: 12px 14px;
	border-radius: 12px;
	border: 1px solid rgba(255, 122, 146, 0.35);
	background: rgba(255, 80, 110, 0.08);
	color: ${THEME.error};
	font-size: 0.82rem;
	text-align: center;
	line-height: 1.4;
`;

export const SecureNote = styled.p`
	margin: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	color: ${THEME.soft};
	font-size: 0.72rem;
	line-height: 1.4;
	text-align: center;

	&::before {
		content: '🔒';
		font-size: 0.68rem;
		opacity: 0.85;
	}
`;

export const LegalText = styled.p`
	margin: 0;
	font-size: 0.72rem;
	color: rgba(164, 120, 183, 0.95);
	text-align: center;
	line-height: 1.45;

	a {
		color: ${THEME.gold};
		text-decoration: underline;
		text-underline-offset: 2px;
	}
`;

export const BillingNote = styled.p`
	margin: 0;
	color: ${THEME.muted};
	font-size: 0.78rem;
	line-height: 1.4;
	text-align: center;
`;

export const BackButton = styled.button`
	display: block;
	margin: 12px auto 0;
	border: none;
	background: transparent;
	color: ${THEME.soft};
	font-size: 0.8rem;
	text-decoration: underline;
	cursor: pointer;
`;
